/**
 * Reliable dev/prod environment detection for the web build.
 *
 * Why this exists: relying on `process.env.NODE_ENV` alone is brittle in
 * the craco/CRA pipeline — webpack only string-replaces the literal
 * `process.env.NODE_ENV` token, optional chaining breaks the rewrite, and
 * the value is `"production"` for any deploy (including alpha-dev). We
 * want the dev tools to show on:
 *   - the local dev server (`yarn web` → NODE_ENV === 'development')
 *   - the alpha-dev deploy (https://alpha-dev.quiklearn.ai, etc.)
 *
 * The helper combines both signals and additionally honors a manual
 * override via `localStorage.quiklearn_force_dev = 'true'` for QA on
 * production-looking hosts.
 */
import { WEB_APP_BASE_URL } from 'config/app.config';

const DEV_HOSTNAME_PATTERN = /(^|\.)(localhost|0\.0\.0\.0|127\.0\.0\.1)$/i;
const DEV_DOMAIN_PATTERN = /(^|\.)(dev|staging|alpha-dev|app-dev|web-dev)\./i;
const DEV_DOMAIN_SUFFIX_PATTERN = /-dev(\.|$)/i;

const FORCE_DEV_LS_KEY = 'quiklearn_force_dev';

function readForceDevOverride() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(FORCE_DEV_LS_KEY) === 'true';
  } catch {
    return false;
  }
}

function isDevHostname(hostname) {
  if (!hostname) return false;
  if (DEV_HOSTNAME_PATTERN.test(hostname)) return true;
  if (DEV_DOMAIN_PATTERN.test(hostname)) return true;
  if (DEV_DOMAIN_SUFFIX_PATTERN.test(hostname)) return true;
  return false;
}

function isDevConfiguredBaseUrl() {
  const base = String(WEB_APP_BASE_URL || '');
  if (!base) return false;
  try {
    const { hostname } = new URL(base);
    return isDevHostname(hostname);
  } catch {
    return /-dev|localhost|staging/i.test(base);
  }
}

/**
 * True when running locally (CRA dev server) or on a dev/staging deploy.
 * Result is computed once at module load and cached.
 */
export const isDevelopmentEnvironment = (() => {
  if (readForceDevOverride()) return true;

  if (
    typeof process !== 'undefined' &&
    process.env &&
    process.env.NODE_ENV &&
    process.env.NODE_ENV !== 'production' &&
    process.env.NODE_ENV !== 'test'
  ) {
    return true;
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    if (isDevHostname(window.location.hostname)) return true;
  }

  if (isDevConfiguredBaseUrl()) return true;

  return false;
})();

export default isDevelopmentEnvironment;
